import type { ComponentChildren } from "preact"
import { useState, useMemo, useEffect, useRef } from "preact/hooks"
import { ChevronRight, ChevronDown, TrendingUp, TrendingDown, X, Menu, Pencil } from "lucide-react"
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Button } from "../../../components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu"
import { ChartContainer } from "../../../components/ui/chart"
import { Input } from "../../../components/ui/input"
import { Checkbox } from "../../../components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { 
  SalesHierarchyNode,
  HierarchyNode,
  HierarchyLevel, 
  TimePeriod, 
  ForecastingViewState,
  TableRow,
  ViewMode,
  ColumnConfig,
  SalesDataPoint,
} from '../types'
import {
  computeComparableDollarTotalsByPeriod,
  sumLeafMonthlyMetricsByPeriod,
} from "../utils/forecast_table_pipeline"
import {
  build_monthly_data_from_points,
  calculate_totals_from_points,
} from "../utils/monthly_metrics_view"
import { format_period_header, MONTHS_PER_WEEK, parse_period_key_to_time_period, shift_week_period_key } from "../utils/periods"
import { fill_forward_periods, fill_forward_values, grow_forward_values, type GrowableField } from "../utils/fill_forward"
import { toast } from "../../../components/ui/use-toast"
import { ToastAction } from "../../../components/ui/toast"
import {
  filter_hierarchy_by_search,
  normalize_search_term,
  read_grid_sort_order,
  save_grid_sort_order,
  sort_hierarchy,
  type GridSortOrder,
} from "../utils/grid_find"

/**
 * Column title that renders the **ship week** first when a lead time is active (week grain):
 * the buyer plans the week the product sells, ops reads the week it must ship.
 */
function ShipAwarePeriodHeader({
  period,
  shipWeekLeadTime,
}: {
  period: TimePeriod
  shipWeekLeadTime: number | null
}) {
  const shipKey =
    shipWeekLeadTime != null ? shift_week_period_key(period.period_key, -shipWeekLeadTime) : null
  if (!shipKey) {
    return <div className="text-sm">{format_period_header(period)}</div>
  }
  return (
    <div>
      <div className="text-sm text-sky-800">
        Ship {format_period_header(parse_period_key_to_time_period(shipKey))}
      </div>
      <div className="text-[11px] font-normal text-gray-500">
        sells {format_period_header(period)}
      </div>
    </div>
  )
}
import {
  flattenHierarchyForTable,
  type EditableMetricField
} from '../utils/aggregation'
import {
  compute_velocity,
  format_velocity,
  quantity_from_velocity,
  velocity_edit_changes_quantity,
} from "../utils/velocity"
import { cases_display, cases_from_units, node_units_per_case, point_quantity_basis } from "../utils/quantity_basis"
import { UNITS_PER_CASE_UNKNOWN_HINT } from "../utils/units_conversion"
import { inline_edit_key_action, parse_cell_input, type CellInputField } from "../utils/cell_input"
import { exactCurrencyFormatter } from '../../../utils'
import type { MetricMode } from '../types'
import {
  effective_quantity_mode,
  quantity_label,
  FORECAST_DETAIL,
  imputed_promo_lift_revenue,
  is_delivered_detail_channel,
} from "../utils/forecast_measure_slice"
import { CompareModeRevenueFooterCell } from "./compare_mode_footer"
import { CustomerSourceBadge } from "./customer_source_badge"
import type { CustomerOverlay } from "../api/forecast_catalog_api"
import { pack_caption, PACKS_VIEW_HINT } from "../utils/catalog_overlay"

type ForecastChartMetric = "volume_dollars" | "cases_sold" | "store_count" | "units_sold"

/** Revenue is what a cell is read for; every period cell, total and footer uses this size. */
export const REVENUE_CELL_CLASS = "text-sm font-semibold tabular-nums text-gray-900"
/** Stores × quantity sits under the revenue, smaller and quieter. */
export const QUANTITY_CELL_CLASS = "text-xs font-normal text-gray-500"

/** Which columns exist in the loaded hierarchy (drives toolbar enablement). */
type TableColumnAvailability = Pick<ColumnConfig, "show_stores" | "show_cases" | "show_volume"> & {
  show_units: boolean
}

/** Min `ch` width for the units column so base and promo counts share one column (commas line up with tabular-nums). */
function promo_units_numeric_min_ch(base_units_display: string, promo_formatted: string): number {
  const b = base_units_display === "—" ? 1 : base_units_display.length
  return Math.max(b, promo_formatted.length, 4)
}

/** Two-column micro-grid: [+][number] with invisible + on base row so digits align with promo row. */
function PromoUnitsDigitPair(props: {
  show_plus: boolean
  number_text: string
  min_ch: number
  className: string
}) {
  const { show_plus, number_text, min_ch, className } = props
  const tpl = `minmax(1ch,1ch) minmax(${min_ch}ch,max-content)`
  return (
    <div
      className={`inline-grid items-baseline justify-items-end ${className}`}
      style={{ gridTemplateColumns: tpl }}
    >
      <span className={show_plus ? "text-center" : "invisible w-[1ch] text-center select-none"} aria-hidden={!show_plus}>
        +
      </span>
      <span className="text-right tabular-nums">{number_text}</span>
    </div>
  )
}

/** Promo $ stacked under base revenue; +promo units aligned in the third column under base units. */
function ForecastPromoMetricRows(props: {
  show_volume: boolean
  volume_dollars: number
  promo_lift_dollars: number
  show_stores: boolean
  store_count: number
  base_units_display: string
  promo_units: number
}) {
  const {
    show_volume,
    volume_dollars,
    promo_lift_dollars,
    show_stores,
    store_count,
    base_units_display,
    promo_units,
  } = props
  const pu = format_promo_incremental_units(promo_units)
  const qty_min_ch = promo_units_numeric_min_ch(base_units_display, pu)
  return (
    <div className="flex w-max flex-col gap-0.5 text-right">
      {show_volume ? (
        <div className="flex flex-col items-end gap-0.5">
          <div className={REVENUE_CELL_CLASS}>{exactCurrencyFormatter(volume_dollars)}</div>
          <div className="text-xs font-semibold tabular-nums text-amber-900">
            {exactCurrencyFormatter(promo_lift_dollars)}
          </div>
        </div>
      ) : null}
      {show_stores ? (
        <div className="inline-grid grid-cols-[auto_auto_auto] items-baseline justify-items-end gap-x-1 gap-y-0.5">
          <span className={`${QUANTITY_CELL_CLASS} tabular-nums`}>{store_count.toLocaleString()}</span>
          <span className={QUANTITY_CELL_CLASS}>×</span>
          <PromoUnitsDigitPair
            show_plus={false}
            number_text={base_units_display}
            min_ch={qty_min_ch}
            className={QUANTITY_CELL_CLASS}
          />
          <span className="col-span-2" aria-hidden="true" />
          <div className="flex flex-col items-end gap-0 leading-tight text-xs font-semibold tabular-nums text-amber-900">
            <PromoUnitsDigitPair show_plus={true} number_text={pu} min_ch={qty_min_ch} className="" />
            <span className="text-[10px] font-medium text-amber-900/90">units</span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-end gap-0.5">
          <PromoUnitsDigitPair
            show_plus={false}
            number_text={base_units_display}
            min_ch={qty_min_ch}
            className={QUANTITY_CELL_CLASS}
          />
          <div className="flex flex-col items-end gap-0 leading-tight text-xs font-semibold tabular-nums text-amber-900">
            <PromoUnitsDigitPair show_plus={true} number_text={pu} min_ch={qty_min_ch} className="" />
            <span className="text-[10px] font-medium text-amber-900/90">units</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** Revenue first, then optional Stores × units|cases (read-only stack for footer / non-edit cells). */
interface PeriodMetricsStackProps {
  columnConfig: ColumnConfig
  effective_qty: "units" | "cases"
  volume_dollars: number
  units_sold?: number | null
  cases_sold: number
  /** Part-cases behind a zero case count (see `cases_display`). */
  part_cases?: number
  store_count: number
  /** Σ SKU-level store counts under this row (velocity denominator on aggregates). */
  sku_store_count?: number
  /** When true with units mode and positive promo sums, show promo lines (Promo lift toolbar). */
  show_promo_lift?: boolean
  promo_lift_dollars?: number
  promo_lift_units?: number
}

function PeriodMetricsStack({
  columnConfig,
  effective_qty,
  volume_dollars,
  units_sold,
  cases_sold,
  part_cases,
  store_count,
  sku_store_count,
  show_promo_lift = false,
  promo_lift_dollars = 0,
  promo_lift_units = 0,
}: PeriodMetricsStackProps) {
  const show_vol = columnConfig.show_volume !== false
  const show_stores = columnConfig.show_stores
  const units_mode = effective_qty === "units"

  function qty_display(): string {
    return units_mode
      ? units_sold === null || units_sold === undefined
        ? "—"
        : Number(units_sold).toLocaleString()
      : cases_display(cases_sold, part_cases)
  }

  const secondary = show_stores
    ? `${store_count.toLocaleString()} × ${qty_display()}`
    : `${qty_display()}`

  // Velocity only means anything against a door count, so it hides when stores are hidden.
  // Aggregate rows divide by the SUM of their SKUs' store counts (points of distribution),
  // not the widest SKU's doors — 16 SKUs' units over one SKU's doors read as 78/store while
  // every child showed 7–13. With the sum, a parent's velocity is its SKUs' average.
  const velocity_denominator = sku_store_count && sku_store_count > 0 ? sku_store_count : store_count
  const velocity =
    columnConfig.show_velocity && show_stores
      ? compute_velocity(units_mode ? units_sold : cases_sold, velocity_denominator)
      : null

  const show_promo_block =
    show_promo_lift && units_mode && (promo_lift_units > 0 || promo_lift_dollars > 0)

  if (show_promo_block) {
    return (
      <div className="mx-auto w-max">
        <ForecastPromoMetricRows
          show_volume={show_vol}
          volume_dollars={volume_dollars}
          promo_lift_dollars={promo_lift_dollars}
          show_stores={show_stores}
          store_count={store_count}
          base_units_display={qty_display()}
          promo_units={promo_lift_units}
        />
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {show_vol ? (
        <div className={REVENUE_CELL_CLASS}>{exactCurrencyFormatter(volume_dollars)}</div>
      ) : null}
      <div
        className={QUANTITY_CELL_CLASS}
        title={units_mode && (units_sold === null || units_sold === undefined) ? UNITS_PER_CASE_UNKNOWN_HINT : undefined}
      >
        {secondary}
      </div>
      {columnConfig.show_velocity && show_stores ? (
        <div className="text-[11px] tabular-nums text-gray-500" title={`${units_mode ? "Units" : "Cases"} per store`}>
          {format_velocity(velocity)}/store
        </div>
      ) : null}
    </div>
  )
}

interface ForecastLineChartPoint {
  period_key: string
  period_label: string
  source_kind: string
  store_count: number
  cases_sold: number
  units_sold: number
  volume_dollars: number
}

/** Subtitle under period / Totals — two lines like cells: revenue, then stores × quantity. */
function ForecastGridColumnMetricLabels({ columnConfig }: { columnConfig: ColumnConfig }) {
  const qty = quantity_label(columnConfig)
  const quantity_caption = columnConfig.show_stores ? `Stores × ${qty}` : qty
  const show_revenue_caption = columnConfig.show_volume !== false
  return (
    <div className="flex flex-col items-center gap-0 leading-snug text-xs text-gray-500">
      {show_revenue_caption ? <div>Revenue</div> : null}
      <div>{quantity_caption}</div>
      {columnConfig.show_velocity && columnConfig.show_stores ? <div>Per store</div> : null}
    </div>
  )
}

/** Mix a hex accent with white for table surfaces (invalid hex → neutral gray). */
function mixHexWithWhite(hexColor: string, colorWeight: number): string {
  const hex = hexColor.replace("#", "").trim()
  const full = hex.length === 3 ? hex.split("").map((c: string) => c + c).join("") : hex
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return "rgb(243, 244, 246)"
  }
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  const t = Math.max(0, Math.min(1, colorWeight))
  return `rgb(${Math.round(r * t + 255 * (1 - t))}, ${Math.round(g * t + 255 * (1 - t))}, ${Math.round(b * t + 255 * (1 - t))})`
}

function opportunityRowBackgroundColor(hexColor: string): string {
  return mixHexWithWhite(hexColor, 0.14)
}

function opportunityBadgeBackgroundColor(hexColor: string): string {
  return mixHexWithWhite(hexColor, 0.32)
}

function buildForecastLineChartData(row: TableRow, periods: TimePeriod[]): ForecastLineChartPoint[] {
  return periods
    .map((period: TimePeriod) => {
      const periodData = row.monthly_data[period.period_key]
      if (!periodData) return null

      return {
        period_key: period.period_key,
        period_label: format_period_header(period),
        source_kind: periodData.is_actual ? "Actual" : "Forecast",
        store_count: periodData.store_count,
        cases_sold: periodData.cases_sold,
        units_sold: Number(periodData.units_sold ?? 0),
        volume_dollars: periodData.volume_dollars,
      }
    })
    .filter((point: ForecastLineChartPoint | null): point is ForecastLineChartPoint => point !== null)
}

function getForecastChartMetricLabel(metric: ForecastChartMetric): string {
  if (metric === "volume_dollars") return "Revenue"
  if (metric === "cases_sold") return "Cases"
  if (metric === "units_sold") return "Units"
  return "Stores"
}

function formatForecastChartValue(value: number, metric: ForecastChartMetric): string {
  if (metric === "volume_dollars") return exactCurrencyFormatter(value)
  return Math.round(value).toLocaleString()
}

// Component for displaying comparison metrics with visual indicators
interface ComparisonMetricsProps {
  actual: number
  forecast: number
  variance: number
  variancePct: number
  label: string
  format: 'number' | 'currency' | 'percentage'
  compact?: boolean
  actualBadgeLabel?: string
  forecastBadgeLabel?: string
  /** Version compare: a $0 compared box on every unmatched cell is pure noise — drop it. */
  hideZeroForecast?: boolean
}

function ComparisonMetrics({
  actual,
  forecast,
  variance,
  variancePct,
  label,
  format,
  compact = false,
  actualBadgeLabel = "Actual",
  forecastBadgeLabel = "Forecast",
  hideZeroForecast = false,
}: ComparisonMetricsProps) {
  const isPositive = variance >= 0
  const isSignificant = Math.abs(variancePct) >= 5 // 5% threshold for significance
  const roundedVariancePct = Number(variancePct.toFixed(1))
  const actualEqualsForecast = Math.abs(actual - forecast) < 0.0001
  const shouldHidePctAsRoundingNoise = roundedVariancePct === 0 && !actualEqualsForecast
  const shouldShowTrend = !shouldHidePctAsRoundingNoise
  const trendText = `${roundedVariancePct > 0 ? "+" : ""}${roundedVariancePct.toFixed(1)}%`
  
  function formatValue(value: number): string {
    switch (format) {
      case 'currency':
        return exactCurrencyFormatter(value)
      case 'percentage':
        return `${value.toFixed(1)}%`
      default:
        return value.toLocaleString()
    }
  }
  
  const suppress_forecast_row = hideZeroForecast && Math.abs(forecast) < 0.005

  if (compact) {
    return (
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              {actualBadgeLabel}
            </span>
            <span className="text-sm font-semibold">{formatValue(actual)}</span>
          </div>
          {!suppress_forecast_row ? (
            <div className="flex items-center gap-1.5">
              <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                {forecastBadgeLabel}
              </span>
              <span className="text-sm text-gray-600">{formatValue(forecast)}</span>
            </div>
          ) : null}
        </div>
        {shouldShowTrend ? (
          <div className={`flex items-center gap-1 text-xs ${
            isSignificant
              ? (isPositive ? "text-green-600" : "text-red-600")
              : "text-gray-500"
          }`}>
            {roundedVariancePct !== 0 ? (
              isPositive ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )
            ) : null}
            <span>
              {trendText}
            </span>
          </div>
        ) : null}
      </div>
    )
  }
  
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{formatValue(actual)}</span>
        <span className="text-xs text-gray-500">vs</span>
        <span className="text-sm text-gray-600">{formatValue(forecast)}</span>
      </div>
      <div className={`flex items-center gap-1 text-xs ${
        isSignificant 
          ? (isPositive ? 'text-green-600' : 'text-red-600')
          : 'text-gray-500'
      }`}>
        {isPositive ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        <span>
          {isPositive ? '+' : ''}{formatValue(variance)} ({isPositive ? '+' : ''}{variancePct.toFixed(1)}%)
        </span>
      </div>
    </div>
  )
}

/** Compare forecast vs actual for the metric the user is viewing ($ / cases / units). */
interface ExpandableComparisonProps {
  metric_mode: MetricMode
  comparisonData: {
    forecast_store_count: number
    forecast_volume_dollars: number
    forecast_cases_sold: number
    forecast_units_sold?: number | null
    actual_store_count: number
    actual_volume_dollars: number
    actual_cases_sold: number
    actual_units_sold?: number | null
    store_count_variance: number
    volume_dollars_variance: number
    cases_sold_variance: number
    store_count_variance_pct: number
    volume_dollars_variance_pct: number
    cases_sold_variance_pct: number
    actual_badge_label?: string
    forecast_badge_label?: string
  }
}

function format_promo_incremental_units(n: number): string {
  return Math.round(n).toLocaleString()
}

function cell_imputed_promo_dollars(
  detail: Record<string, number> | undefined,
  volume_dollars: number,
  display_units: number | null | undefined,
): number {
  if (!detail) {
    return 0
  }
  const p = detail[FORECAST_DETAIL.incremental_promo_units]
  if (p === undefined || !Number.isFinite(p) || p <= 0) {
    return 0
  }
  return imputed_promo_lift_revenue(volume_dollars, display_units, p) ?? 0
}

function cell_promo_incremental_units(detail: Record<string, number> | undefined): number {
  if (!detail) {
    return 0
  }
  const p = detail[FORECAST_DETAIL.incremental_promo_units]
  return typeof p === "number" && Number.isFinite(p) && p > 0 ? p : 0
}

function detail_has_positive_forecasted_promo(detail: Record<string, number> | undefined): boolean {
  if (!detail) {
    return false
  }
  const p = detail[FORECAST_DETAIL.incremental_promo_units]
  return typeof p === "number" && Number.isFinite(p) && p > 0
}

function ExpandableComparison({ metric_mode, comparisonData }: ExpandableComparisonProps) {
  const actualBadge = comparisonData.actual_badge_label ?? "Actual"
  const forecastBadge = comparisonData.forecast_badge_label ?? "Forecast"
  // Only labeled compares (version compare, top-down preview) suppress $0 compared boxes;
  // the plain forecast-vs-actual view keeps both sides for context.
  const hideZeroForecast = comparisonData.forecast_badge_label != null
  if (metric_mode === "dollars") {
    return (
      <ComparisonMetrics
        actual={comparisonData.actual_volume_dollars}
        forecast={comparisonData.forecast_volume_dollars}
        variance={comparisonData.volume_dollars_variance}
        variancePct={comparisonData.volume_dollars_variance_pct}
        label="Revenue"
        format="currency"
        compact={true}
        actualBadgeLabel={actualBadge}
        forecastBadgeLabel={forecastBadge}
        hideZeroForecast={hideZeroForecast}
      />
    )
  }
  if (metric_mode === "cases") {
    return (
      <ComparisonMetrics
        actual={comparisonData.actual_cases_sold}
        forecast={comparisonData.forecast_cases_sold}
        variance={comparisonData.cases_sold_variance}
        variancePct={comparisonData.cases_sold_variance_pct}
        label="Cases"
        format="number"
        compact={true}
        actualBadgeLabel={actualBadge}
        forecastBadgeLabel={forecastBadge}
        hideZeroForecast={hideZeroForecast}
      />
    )
  }
  const forecast_u = Number(comparisonData.forecast_units_sold ?? 0)
  const actual_u = Number(comparisonData.actual_units_sold ?? 0)
  const variance_u = actual_u - forecast_u
  const variance_u_pct = forecast_u !== 0 ? (variance_u / forecast_u) * 100 : 0
  return (
    <ComparisonMetrics
      actual={actual_u}
      forecast={forecast_u}
      variance={variance_u}
      variancePct={variance_u_pct}
      label="Units"
      format="number"
      compact={true}
      actualBadgeLabel={actualBadge}
      forecastBadgeLabel={forecastBadge}
      hideZeroForecast={hideZeroForecast}
    />
  )
}

interface SalesForecastingTableProps {
  data: SalesHierarchyNode[]
  periods: TimePeriod[]
  viewMode: ViewMode
  onViewModeChange?: (viewMode: ViewMode) => void
  forecastConfig: any
  compareMode?: boolean
  comparisonPeriods?: TimePeriod[]
  columnConfig: ColumnConfig
  onDataChange?: (rowId: string, periodKey: string, field: EditableMetricField, value: number, options?: MetricEditOptions) => void
  onForecastConfigChange?: (rowId: string, newConfig: { same_store_sales_multiplier: number, store_count_growth_multiplier: number }) => void
  expandedNodes?: Set<string>
  onToggleExpansion?: (nodeId: string) => void
  onExpandAll?: () => void
  onCollapseAll?: () => void
  onColumnConfigChange?: (config: ColumnConfig) => void
  onCreateOpportunityFromRow?: (row: TableRow) => void
  onCreateSalesSourceFromRow?: (row: TableRow) => void
  onDeleteHierarchyRow?: (row: TableRow) => void
  onAddHierarchyRow?: (row: TableRow) => void
  onAddSkuFromCatalog?: (row: TableRow) => void
  /** Reopens a line's setup (doors, cases, growth, price, start) to change it. */
  onEditLine?: (row: TableRow) => void
  onHierarchyRowNameChange?: (rowId: string, name: string) => void
  /** Rendered next to column checkboxes (e.g. Opportunities toggle). */
  tableToolbarExtra?: ComponentChildren
  /** Metric / slice / Forecast vs Compare / Promo lift — left of Columns. */
  reviewToolbar?: ComponentChildren
  /** Retailer detail: stacked baseline vs forecast vs promo incremental units (units mode only). */
  promo_lift_compare?: boolean
  /** When top-down preview is active: badges and footer caption for compare cells. */
  compareBadgeLabels?: { actual: string; forecast: string }
  compareFooterSubsetCaption?: string
  /**
   * Week-grain only: lead time in weeks between shipping and consumption. When set, column
   * headers show the **ship week** (consumption − lead time) with the consumption week beneath.
   * Display-only — no data moves.
   */
  shipWeekLeadTime?: number | null
  /**
   * Ordered period keys fill-forward walks, including weeks not currently shown as columns.
   * Defaults to `periods` so a clipped rolling window still carries a store-count edit into
   * the next calendar year.
   */
  fillForwardPeriodKeys?: string[]
  className?: string
}

export function SalesForecastingTable({ 
  data, 
  periods, 
  viewMode, 
  onViewModeChange,
  forecastConfig, 
  compareMode = false, 
  comparisonPeriods = [], 
  columnConfig,
  onDataChange, 
  onForecastConfigChange,
  expandedNodes = new Set(),
  onToggleExpansion,
  onExpandAll,
  onCollapseAll,
  onColumnConfigChange,
  onCreateOpportunityFromRow,
  onCreateSalesSourceFromRow,
  onDeleteHierarchyRow,
  onAddHierarchyRow,
  onAddSkuFromCatalog,
  onEditLine,
  onHierarchyRowNameChange,
  tableToolbarExtra,
  reviewToolbar,
  promo_lift_compare = false,
  compareBadgeLabels,
  compareFooterSubsetCaption,
  shipWeekLeadTime = null,
  fillForwardPeriodKeys,
  className 
}: SalesForecastingTableProps) {
  // View state management
  const [chartRow, setChartRow] = useState<TableRow | null>(null)
  const [viewState, setViewState] = useState<ForecastingViewState>({
    expanded_nodes: expandedNodes,
    visible_levels: new Set(Object.values(HierarchyLevel)), // Show all levels by default
    selected_periods: periods,
    search_term: '',
    sort_by: 'stores',
    sort_direction: 'desc',
    show_forecast_only: false,
    show_actual_only: false,
    view_mode: viewMode,
    forecast_config: forecastConfig,
    compare_mode: compareMode,
    comparison_periods: comparisonPeriods,
    column_config: columnConfig
  })

  // Row order is the viewer's own preference, not part of the plan: kept in this browser.
  const [sortOrder, setSortOrder] = useState<GridSortOrder>(() => read_grid_sort_order())
  function handleSortOrderChange(order: GridSortOrder) {
    setSortOrder(order)
    save_grid_sort_order(order)
  }

  // The search box updates as it is typed; the grid follows a beat later, because a match
  // under a collapsed parent opens that parent and a broad term can open most of the plan.
  const [searchInput, setSearchInput] = useState("")
  useEffect(() => {
    const timer = setTimeout(() => {
      setViewState(prev => (prev.search_term === searchInput ? prev : { ...prev, search_term: searchInput }))
    }, 200)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Sync expanded nodes with prop
  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      expanded_nodes: expandedNodes
    }))
  }, [expandedNodes])

  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      selected_periods: periods
    }))
  }, [periods])

  // Note: Forecasts load collapsed by default; users can expand specific rows or use Expand All.

  const availableColumns = useMemo(() => getAvailableColumns(data), [data])
  const effectiveColumnConfig = useMemo((): ColumnConfig => {
    // Units is always offered: cells derive units from cases × the item's pack size, and a row
    // with no pack size shows an em dash with a hint — the mode is no longer forced back to cases.
    return {
      ...columnConfig,
      show_stores: columnConfig.show_stores && availableColumns.show_stores,
      show_cases: columnConfig.show_cases && availableColumns.show_cases,
      show_volume: columnConfig.show_volume && availableColumns.show_volume,
      quantity_mode: columnConfig.quantity_mode === "packs" ? "packs" : effective_quantity_mode(columnConfig),
    }
  }, [availableColumns, columnConfig])

  // Order, then search, on the tree itself — before flattening, which only keeps what is
  // expanded. Searching the flattened rows could not find anything under a collapsed parent.
  const searchResult = useMemo(
    () => filter_hierarchy_by_search(sort_hierarchy(data, sortOrder), viewState.search_term),
    [data, sortOrder, viewState.search_term]
  )
  const searchActive = normalize_search_term(viewState.search_term) !== ""
  /** What is open on screen: the user's own expansions plus the path to each search match. */
  const visibleExpandedNodes = useMemo(() => {
    if (searchResult.expand_ids.size === 0) return viewState.expanded_nodes
    return new Set<string>([...viewState.expanded_nodes, ...searchResult.expand_ids])
  }, [viewState.expanded_nodes, searchResult])

  // Flatten hierarchy for table display (use effective column config so cells match slice + availability)
  const tableRows = useMemo(() => {
    if (viewMode === ViewMode.PRODUCT_LINE_VIEW) {
      // For product line view, we need to handle the hierarchical structure
      const rows: any[] = []
      
      function processNode(node: any, depth: number = 0) {
        const isExpanded = visibleExpandedNodes.has(node.id)
        
        // Add the current node
        rows.push({
          id: node.id,
          name: node.name,
          level: node.level,
          depth: depth,
          parent_id: node.parent_id,
          is_expanded: isExpanded,
          is_visible: true,
          has_children: node.children && node.children.length > 0,
          monthly_data: build_monthly_data_from_points(node.sales_data, effectiveColumnConfig),
          totals: calculate_totals_from_points(node.sales_data, effectiveColumnConfig),
          forecast_config: node.forecast_config,
          metadata: node.metadata,
          case_cost: (node as any).case_cost,
          product_sku: (node as any).product_sku,
          sku_name: (node as any).sku_name
        })
        
        // Add children if expanded
        if (isExpanded && node.children && node.children.length > 0) {
          node.children.forEach((child: any) => processNode(child, depth + 1))
        }
      }
      
      searchResult.nodes.forEach(node => processNode(node))
      return rows
    }
    
    
    // Show all levels by default (no filtering)
    const allLevels = new Set(Object.values(HierarchyLevel))
    return flattenHierarchyForTable(searchResult.nodes, visibleExpandedNodes, allLevels, 0, effectiveColumnConfig)
  }, [searchResult, visibleExpandedNodes, viewMode, effectiveColumnConfig])

  // Apply search filter. A match keeps its whole subtree (so the row still expands down to
  // SKUs) and its ancestors (so the path reads in context) — filtering to matching names
  // alone made every hit a dead end. The rule lives in `filter_hierarchy_by_search`, applied
  // to the tree above so it also reaches rows under a collapsed parent.
  const filteredRows = tableRows
  const actualPeriodKeys = useMemo(
    () => new Set(comparisonPeriods.map((period: TimePeriod) => period.period_key)),
    [comparisonPeriods]
  )

  /** Leaf-only sums per period — footer totals avoid double-counting expanded parent rows. */
  const leafPeriodTotals = useMemo(
    () =>
      sumLeafMonthlyMetricsByPeriod(
        data as SalesHierarchyNode[],
        viewState.selected_periods.map((p: TimePeriod) => p.period_key),
        effectiveColumnConfig
      ),
    [data, viewState.selected_periods, effectiveColumnConfig]
  )
  const comparableDollarTotalsByPeriod = useMemo(() => {
    const periodKeys = viewState.selected_periods.map((p: TimePeriod) => p.period_key)
    return computeComparableDollarTotalsByPeriod(data as SalesHierarchyNode[], periodKeys)
  }, [data, viewState.selected_periods])

  const footerGrandTotals = useMemo(() => {
    let total_volume = 0
    let total_cases = 0
    let total_part_cases = 0
    let total_stores = 0
    let total_sku_stores = 0
    let total_units = 0
    let total_promo_lift_dollars = 0
    let total_promo_lift_units = 0
    for (const period of viewState.selected_periods) {
      const s = leafPeriodTotals[period.period_key]
      if (s) {
        total_volume += s.volume_dollars
        total_cases += s.cases_sold
        total_part_cases += s.part_cases
        // Doors are concurrent reach — max across periods, not a sum (see `max_store_count`).
        total_stores = Math.max(total_stores, s.store_count)
        total_sku_stores = Math.max(total_sku_stores, s.sku_store_count)
        total_units += Number(s.units_sold ?? 0)
        total_promo_lift_dollars += s.promo_lift_dollars
        total_promo_lift_units += s.promo_lift_units
      }
    }
    return {
      total_volume,
      total_cases,
      total_part_cases,
      total_stores,
      total_sku_stores,
      total_units,
      total_promo_lift_dollars,
      total_promo_lift_units,
    }
  }, [leafPeriodTotals, viewState.selected_periods])

  const display_qty_mode = effective_quantity_mode(effectiveColumnConfig)
  const footer_show_promo_lift = useMemo(
    () =>
      promo_lift_compare &&
      !is_delivered_detail_channel(effectiveColumnConfig) &&
      display_qty_mode === "units",
    [promo_lift_compare, effectiveColumnConfig, display_qty_mode],
  )
  const showCaseCostColumn = display_qty_mode === "cases" && availableColumns.show_cases
  const showForecastControlsColumn =
    effectiveColumnConfig.show_stores ||
    (display_qty_mode === "cases" && availableColumns.show_cases)
  const isAnyNodeExpanded = expandedNodes.size > 0
  const showActualForecastHeaders = actualPeriodKeys.size > 0


  // Event handlers
  function toggleNodeExpansion(nodeId: string) {
    if (onToggleExpansion) {
      onToggleExpansion(nodeId)
    } else {
      setViewState(prev => ({
        ...prev,
        expanded_nodes: prev.expanded_nodes.has(nodeId)
          ? new Set([...prev.expanded_nodes].filter(id => id !== nodeId))
          : new Set([...prev.expanded_nodes, nodeId])
      }))
    }
  }

  function handleDataChange(
    rowId: string,
    periodKeyOrField: string,
    field: EditableMetricField,
    value: number,
    options?: MetricEditOptions,
  ): void {
    if (onDataChange) {
      if (field === 'case_cost') {
        // For case_cost, we don't need a period key, just pass the field and value
        onDataChange(rowId, '', field, value)
      } else {
        // `options` carries the later periods a carry-forward resolved; dropping it here turns
        // every carry-forward into a one-period edit.
        onDataChange(rowId, periodKeyOrField, field, value, options)
      }
    }
  }


  function handleSearchChange(term: string) {
    setSearchInput(term)
  }


  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Search Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Left: Search */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Search:</label>
              <div className="relative">
                <Input
                  placeholder="Find a customer or product..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.currentTarget.value)}
                  className="w-64 pr-8"
                />
                {searchInput ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSearchChange("")}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-gray-400 hover:text-gray-700"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
              {searchActive ? (
                <span className="text-xs text-gray-500">
                  {searchResult.match_count.toLocaleString()} match{searchResult.match_count === 1 ? "" : "es"}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort:</label>
              <Select value={sortOrder} onValueChange={(value: string) => handleSortOrderChange(value as GridSortOrder)}>
                <SelectTrigger className="h-9 w-48 bg-white text-sm" aria-label="Sort rows">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue (high to low)</SelectItem>
                  <SelectItem value="name">Name (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Review (display) + Columns + extra */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {reviewToolbar ? (
              <div className="flex max-w-full flex-wrap items-center gap-2">{reviewToolbar}</div>
            ) : null}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Columns:</span>
              <div className="flex items-center gap-4 rounded-lg bg-gray-50 p-2">
                {availableColumns.show_stores && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-stores"
                      checked={columnConfig.show_stores}
                      onCheckedChange={(checked: boolean) =>
                        onColumnConfigChange?.({ ...columnConfig, show_stores: checked })
                      }
                    />
                    <label htmlFor="show-stores" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Stores
                    </label>
                  </div>
                )}
                {availableColumns.show_stores && columnConfig.show_stores && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-velocity"
                      checked={Boolean(columnConfig.show_velocity)}
                      onCheckedChange={(checked: boolean) =>
                        onColumnConfigChange?.({ ...columnConfig, show_velocity: checked })
                      }
                    />
                    <label
                      htmlFor="show-velocity"
                      className="cursor-pointer text-sm font-medium text-gray-700"
                      title="Quantity per store per period. Editing it holds the store count and moves the quantity."
                    >
                      Velocity
                    </label>
                  </div>
                )}
                {(availableColumns.show_units || availableColumns.show_cases) && (
                  <div className="flex rounded-md border border-gray-300 bg-white p-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title={
                        availableColumns.show_units
                          ? "Units: cases × the item's units per case"
                          : "Units: cases × the item's units per case. Rows without a pack size show —"
                      }
                      onClick={() =>
                        onColumnConfigChange?.({
                          ...columnConfig,
                          quantity_mode: "units",
                        })
                      }
                      className={`h-7 px-2.5 text-xs ${
                        effectiveColumnConfig.quantity_mode === "units"
                          ? "border border-blue-300 bg-blue-50 text-blue-800 shadow-sm"
                          : "border border-transparent text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Units
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title={PACKS_VIEW_HINT}
                      onClick={() =>
                        onColumnConfigChange?.({
                          ...columnConfig,
                          quantity_mode: "packs",
                        })
                      }
                      className={`h-7 px-2.5 text-xs ${
                        effectiveColumnConfig.quantity_mode === "packs"
                          ? "border border-blue-300 bg-blue-50 text-blue-800 shadow-sm"
                          : "border border-transparent text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Packs
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!availableColumns.show_cases}
                      onClick={() =>
                        onColumnConfigChange?.({
                          ...columnConfig,
                          quantity_mode: "cases",
                        })
                      }
                      className={`h-7 px-2.5 text-xs ${
                        display_qty_mode === "cases"
                          ? "border border-blue-300 bg-blue-50 text-blue-800 shadow-sm"
                          : "border border-transparent text-gray-600 hover:bg-gray-100"
                      } ${!availableColumns.show_cases ? "cursor-not-allowed opacity-40" : ""}`}
                    >
                      Cases
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {tableToolbarExtra ? (
              <div className="flex items-center gap-2 border-l border-gray-200 pl-4">{tableToolbarExtra}</div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Data Table */}
      {/* The grid scrolls inside its own box, no taller than the window, so the horizontal
          scrollbar is always on screen — it used to sit under the last row, a full page of
          customers away. Period headers, the hierarchy column and the totals row stay put. */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-[calc(100vh-6rem)] overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 shadow-[0_1px_0_0_#e5e7eb] [&_th]:bg-gray-50">
              {/* Data Type Labels Row */}
              {showActualForecastHeaders && (
                <tr>
                  <th className="text-left px-3 pt-2 pb-0 font-medium text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-64">
                    {/* Empty cell for hierarchy column */}
                  </th>
                  {viewState.selected_periods.map((period, index) => {
                    const isActualPeriod = actualPeriodKeys.has(period.period_key)
                    const nextPeriod = viewState.selected_periods[index + 1]
                    const isLastActualPeriod = isActualPeriod && (!nextPeriod || !actualPeriodKeys.has(nextPeriod.period_key))
                    
                    return (
                      <th 
                        key={`label-${period.period_key}`} 
                        className={`text-center px-2 pt-2 pb-0 font-medium text-xs text-gray-600 min-w-32 relative ${
                          isLastActualPeriod ? 'border-r-2 border-gray-400' : ''
                        }`}
                      >
                        <div className={`inline-flex rounded px-2 py-0.5 ${
                          isActualPeriod 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {isActualPeriod ? 'ACTUAL' : 'FORECAST'}
                        </div>
                      </th>
                    )
                  })}
                  <th className="text-center px-2 pt-2 pb-0 font-medium text-gray-600 min-w-32">
                    {/* Empty cell for totals */}
                  </th>
                  {showCaseCostColumn && (
                    <th className="text-center px-2 pt-2 pb-0 font-medium text-gray-600 min-w-24">
                      {/* Empty cell for case cost */}
                    </th>
                  )}
                  {showForecastControlsColumn && (
                    <th className="text-center px-2 pt-2 pb-0 font-medium text-gray-600 min-w-32">
                      {/* Empty cell for forecast config */}
                    </th>
                  )}
                </tr>
              )}
              
              {/* Main Header Row */}
              <tr>
                <th className="text-left px-3 pt-2 pb-3 font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-80">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={isAnyNodeExpanded ? onCollapseAll : onExpandAll}
                      className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                      title={isAnyNodeExpanded ? "Collapse hierarchy" : "Expand hierarchy"}
                    >
                      {isAnyNodeExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div>
                      <div className="text-sm">Hierarchy</div>
                    </div>
                    <Select
                      value={viewMode}
                      onValueChange={(value: string) => onViewModeChange?.(value as ViewMode)}
                    >
                      <SelectTrigger className="h-8 w-36 bg-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ViewMode.CHAIN_RETAILER_VIEW}>Customer</SelectItem>
                        <SelectItem value={ViewMode.PRODUCT_LINE_VIEW}>Product Line</SelectItem>
                        <SelectItem value={ViewMode.SHIP_TO_VIEW}>Ship-to</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </th>
                {viewState.selected_periods.map((period, index) => {
                  const nextPeriod = viewState.selected_periods[index + 1]
                  const isLastActualPeriod = showActualForecastHeaders
                    && actualPeriodKeys.has(period.period_key)
                    && (!nextPeriod || !actualPeriodKeys.has(nextPeriod.period_key))
                  
                  return (
                    <th 
                      key={period.period_key} 
                      className={`text-center px-3 pt-2 pb-3 font-medium text-gray-700 min-w-32 relative ${
                        isLastActualPeriod ? 'border-r-2 border-gray-400' : ''
                      }`}
                    >
                      <div>
                        <ShipAwarePeriodHeader period={period} shipWeekLeadTime={shipWeekLeadTime} />
                        <ForecastGridColumnMetricLabels columnConfig={effectiveColumnConfig} />
                      </div>
                    </th>
                  )
                })}
                <th className="text-center px-3 pt-2 pb-3 font-medium text-gray-700 min-w-32">
                  <div>
                    <div className="text-sm">Totals</div>
                    <ForecastGridColumnMetricLabels columnConfig={effectiveColumnConfig} />
                  </div>
                </th>
                {showCaseCostColumn && (
                  <th className="text-center px-3 pt-2 pb-3 font-medium text-gray-700 min-w-24">
                    <div>
                      <div className="text-sm">Case Cost</div>
                      <div className="text-xs text-gray-500">$ per case</div>
                    </div>
                  </th>
                )}
                {showForecastControlsColumn && (
                  <th className="text-center px-3 pt-2 pb-3 font-medium text-gray-700 min-w-32">
                    <div>
                      <div className="text-sm">Forecast</div>
                      <div className="text-xs text-gray-500">Stores% / SSS%</div>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(row => (
                <TableRowComponent
                  key={row.id}
                  row={row}
                  viewMode={viewMode}
                  periods={viewState.selected_periods}
                  compareMode={compareMode}
                  promo_lift_compare={promo_lift_compare}
                  compareBadgeLabels={compareBadgeLabels}
                  columnConfig={effectiveColumnConfig}
                  showCaseCostColumn={showCaseCostColumn}
                  showForecastControlsColumn={showForecastControlsColumn}
                  onToggleExpansion={toggleNodeExpansion}
                  onDataChange={handleDataChange}
                  onForecastConfigChange={onForecastConfigChange}
                  onOpenLineChart={setChartRow}
                  onCreateOpportunityFromRow={onCreateOpportunityFromRow}
                  onCreateSalesSourceFromRow={onCreateSalesSourceFromRow}
                  onDeleteHierarchyRow={onDeleteHierarchyRow}
                  onAddHierarchyRow={onAddHierarchyRow}
                  onAddSkuFromCatalog={onAddSkuFromCatalog}
                  onEditLine={onEditLine}
                  onHierarchyRowNameChange={onHierarchyRowNameChange}
                  fillForwardPeriodKeys={fillForwardPeriodKeys}
                />
              ))}
              {searchActive && filteredRows.length === 0 ? (
                <tr>
                  <td className="sticky left-0 px-3 py-6 text-sm text-gray-500">
                    No rows match "{viewState.search_term.trim()}".
                  </td>
                </tr>
              ) : null}
            </tbody>
            <tfoot className="sticky bottom-0 z-20 shadow-[0_-2px_0_0_#d1d5db] [&_td]:bg-gray-50">
              <tr className="border-t-2 border-gray-300 bg-gray-50">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-sm font-semibold text-gray-900">
                  Total
                  {searchActive ? (
                    <div className="text-xs font-normal text-gray-500">Whole plan, not only the rows shown</div>
                  ) : null}
                </td>
                {viewState.selected_periods.map((period: TimePeriod) => {
                  const s = leafPeriodTotals[period.period_key]
                  const comparableTotals = comparableDollarTotalsByPeriod[period.period_key]
                  const hasFooterCompare =
                    compareMode &&
                    (effectiveColumnConfig.metric_mode ?? "dollars") === "dollars" &&
                    comparableTotals &&
                    effectiveColumnConfig.show_volume
                  return (
                    <td key={`foot-${period.period_key}`} className="p-3 text-center align-top">
                      {hasFooterCompare ? (
                        <CompareModeRevenueFooterCell
                          comparableTotals={comparableTotals}
                          fullForecastVolumeDollars={s?.volume_dollars ?? 0}
                          show_promo_lift={footer_show_promo_lift}
                          promo_lift_dollars={s?.promo_lift_dollars ?? 0}
                          promo_lift_units={s?.promo_lift_units ?? 0}
                          subsetCaption={compareFooterSubsetCaption}
                          renderComparison={({ actualVolumeDollars, forecastVolumeDollars, varianceVolumeDollars, varianceVolumePct }) => (
                            <ExpandableComparison
                              metric_mode="dollars"
                              comparisonData={{
                                forecast_store_count: 0,
                                forecast_volume_dollars: forecastVolumeDollars,
                                forecast_cases_sold: 0,
                                actual_store_count: 0,
                                actual_volume_dollars: actualVolumeDollars,
                                actual_cases_sold: 0,
                                store_count_variance: 0,
                                volume_dollars_variance: varianceVolumeDollars,
                                cases_sold_variance: 0,
                                store_count_variance_pct: 0,
                                volume_dollars_variance_pct: varianceVolumePct,
                                cases_sold_variance_pct: 0,
                                ...(compareBadgeLabels
                                  ? {
                                      actual_badge_label: compareBadgeLabels.actual,
                                      forecast_badge_label: compareBadgeLabels.forecast,
                                    }
                                  : {}),
                              }}
                            />
                          )}
                        />
                      ) : (
                        <PeriodMetricsStack
                          columnConfig={effectiveColumnConfig}
                          effective_qty={display_qty_mode}
                          volume_dollars={s?.volume_dollars ?? 0}
                          units_sold={s?.units_sold ?? null}
                          cases_sold={s?.cases_sold ?? 0}
                          part_cases={s?.part_cases}
                          store_count={s?.store_count ?? 0}
                          sku_store_count={s?.sku_store_count}
                          show_promo_lift={footer_show_promo_lift}
                          promo_lift_dollars={s?.promo_lift_dollars ?? 0}
                          promo_lift_units={s?.promo_lift_units ?? 0}
                        />
                      )}
                    </td>
                  )
                })}
                <td className="p-3 text-center align-top">
                  <PeriodMetricsStack
                    columnConfig={effectiveColumnConfig}
                    effective_qty={display_qty_mode}
                    volume_dollars={footerGrandTotals.total_volume}
                    units_sold={footerGrandTotals.total_units}
                    cases_sold={footerGrandTotals.total_cases}
                    part_cases={footerGrandTotals.total_part_cases}
                    store_count={footerGrandTotals.total_stores}
                    sku_store_count={footerGrandTotals.total_sku_stores}
                    show_promo_lift={footer_show_promo_lift}
                    promo_lift_dollars={footerGrandTotals.total_promo_lift_dollars}
                    promo_lift_units={footerGrandTotals.total_promo_lift_units}
                  />
                </td>
                {showCaseCostColumn && (
                  <td className="p-3 text-center text-xs text-gray-400">—</td>
                )}
                {showForecastControlsColumn && (
                  <td className="p-3 text-center text-xs text-gray-400">—</td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {chartRow && (
        <ForecastLineChartModal
          row={chartRow}
          periods={viewState.selected_periods}
          columnConfig={effectiveColumnConfig}
          onClose={() => setChartRow(null)}
        />
      )}
    </div>
  )
}

function hierarchy_row_can_be_deleted(row: TableRow): boolean {
  return row.original_retailer_id == null && row.original_dc_id == null
}

/**
 * Whether a row was generated from a line setup the Edit line dialog can reopen: a leaf (its plan
 * is its own, not a roll-up) that carries `forecast_config`, outside actual and opportunity rows.
 */
export function row_has_line_setup(row: Pick<TableRow, "has_children" | "original_retailer_id" | "original_dc_id"> & { forecast_config?: unknown }): boolean {
  return !row.has_children && row.original_retailer_id == null && row.original_dc_id == null && row.forecast_config != null
}

/**
 * Whether a row's name can be typed over in the grid. A product (SKU) row is named by the item it
 * was added from — its catalog identity is what actuals land on — so it is not renamed in place
 * (1st Phorm, 10/29); remove it and add the right product instead. Customer, channel and product
 * line rows a planner made up stay renameable.
 */
export function hierarchy_row_name_is_editable(row: Pick<TableRow, "level" | "original_retailer_id" | "original_dc_id">): boolean {
  return row.level !== HierarchyLevel.SKU && row.original_retailer_id == null && row.original_dc_id == null
}

interface RowActionsMenuProps {
  row: TableRow
  viewMode: ViewMode
  onAddHierarchyRow?: (row: TableRow) => void
  onAddSkuFromCatalog?: (row: TableRow) => void
  onCreateOpportunityFromRow?: (row: TableRow) => void
  onCreateSalesSourceFromRow?: (row: TableRow) => void
  onDeleteHierarchyRow?: (row: TableRow) => void
}

function RowActionsMenu(props: RowActionsMenuProps) {
  const {
    row,
    viewMode,
    onAddHierarchyRow,
    onAddSkuFromCatalog,
    onCreateOpportunityFromRow,
    onCreateSalesSourceFromRow,
    onDeleteHierarchyRow,
  } = props
  const can_branch = hierarchy_row_can_be_deleted(row)
  const show_add = Boolean(onAddHierarchyRow && viewMode === ViewMode.CHAIN_RETAILER_VIEW && can_branch)
  const show_add_sku = Boolean(
    onAddSkuFromCatalog &&
      viewMode === ViewMode.CHAIN_RETAILER_VIEW &&
      String(row.level) === HierarchyLevel.PRODUCT_LINE
  )
  const show_create_opp = Boolean(onCreateOpportunityFromRow)
  const show_create_source = Boolean(onCreateSalesSourceFromRow)
  const show_delete = Boolean(onDeleteHierarchyRow && can_branch)

  if (!show_add && !show_add_sku && !show_create_opp && !show_create_source && !show_delete) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className="h-7 w-7 shrink-0 text-gray-400 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Row actions"
          title="Row actions"
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <Menu className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 max-w-[92vw]">
        {show_add_sku ? (
          <DropdownMenuItem
            className="flex cursor-pointer flex-col items-start gap-0.5 py-2.5"
            onSelect={() => onAddSkuFromCatalog!(row)}
          >
            <span className="text-sm font-medium text-gray-900">Add SKU from catalog</span>
            <span className="text-xs font-normal leading-snug text-gray-500">
              Pick a catalog product — name, UPC, and case cost come along, so actuals can land on the row.
            </span>
          </DropdownMenuItem>
        ) : null}
        {show_add ? (
          <DropdownMenuItem
            className="flex cursor-pointer flex-col items-start gap-0.5 py-2.5"
            onSelect={() => onAddHierarchyRow!(row)}
          >
            <span className="text-sm font-medium text-gray-900">Add blank hierarchy rows</span>
            <span className="text-xs font-normal leading-snug text-gray-500">
              Insert child rows under this branch so you can enter forecast numbers at the right level (customer, product
              line, or SKU).
            </span>
          </DropdownMenuItem>
        ) : null}
        {show_create_opp ? (
          <DropdownMenuItem
            className="flex cursor-pointer flex-col items-start gap-0.5 py-2.5"
            onSelect={() => onCreateOpportunityFromRow!(row)}
          >
            <span className="text-sm font-medium text-gray-900">Create sales opportunity</span>
            <span className="text-xs font-normal leading-snug text-gray-500">
              Opens the new-source dialog prefilled from this row as a weighted opportunity scenario.
            </span>
          </DropdownMenuItem>
        ) : null}
        {show_create_source ? (
          <DropdownMenuItem
            className="flex cursor-pointer flex-col items-start gap-0.5 py-2.5"
            onSelect={() => onCreateSalesSourceFromRow!(row)}
          >
            <span className="text-sm font-medium text-gray-900">Create sales source</span>
            <span className="text-xs font-normal leading-snug text-gray-500">
              Same hierarchy context as above, but add a standard sales source instead of an opportunity.
            </span>
          </DropdownMenuItem>
        ) : null}
        {show_delete ? (
          <DropdownMenuItem
            className="flex cursor-pointer flex-col items-start gap-0.5 py-2.5 text-red-700 focus:text-red-800"
            onSelect={() => onDeleteHierarchyRow!(row)}
          >
            <span className="text-sm font-medium">Delete this branch</span>
            <span className="text-xs font-normal leading-snug text-red-600/90">
              Remove this row and every nested row beneath it from this forecast.
            </span>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface HierarchyRowNameFieldProps {
  row: TableRow
  hasChildren: boolean
  onCommit: (rowId: string, name: string) => void
}

function HierarchyRowNameField(props: HierarchyRowNameFieldProps) {
  const { row, hasChildren, onCommit } = props
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(row.name)
  const last_prop_name = useRef(row.name)

  if (row.name !== last_prop_name.current) {
    last_prop_name.current = row.name
    if (!editing) {
      setDraft(row.name)
    }
  }

  function commit(): void {
    const trimmed = draft.trim()
    const next = trimmed.length > 0 ? trimmed : "Untitled"
    if (next !== row.name) {
      onCommit(row.id, next)
    }
    setDraft(next)
    setEditing(false)
  }

  function cancel(): void {
    setDraft(row.name)
    setEditing(false)
  }

  if (editing) {
    return (
      <Input
        value={draft}
        onInput={(e: Event) => setDraft((e.target as HTMLInputElement).value)}
        onBlur={() => commit()}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            commit()
          }
          if (e.key === "Escape") {
            cancel()
          }
        }}
        className="h-7 min-w-0 max-w-[min(100%,280px)] flex-1 text-sm"
        autoFocus
      />
    )
  }

  return (
    <span
      className={`min-w-0 max-w-[min(100%,280px)] flex-1 truncate text-sm ${hasChildren ? "font-medium" : ""} cursor-text rounded px-1 hover:bg-gray-100`}
      onClick={() => setEditing(true)}
      title="Click to edit name"
      role="button"
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          setEditing(true)
        }
      }}
    >
      {row.name}
    </span>
  )
}

interface TableRowComponentProps {
  row: TableRow
  viewMode: ViewMode
  periods: TimePeriod[]
  compareMode?: boolean
  promo_lift_compare?: boolean
  columnConfig: ColumnConfig
  showCaseCostColumn: boolean
  showForecastControlsColumn: boolean
  onToggleExpansion: (nodeId: string) => void
  onDataChange?: (rowId: string, periodKey: string, field: EditableMetricField, value: number, options?: MetricEditOptions) => void
  onForecastConfigChange?: (rowId: string, newConfig: { same_store_sales_multiplier: number, store_count_growth_multiplier: number }) => void
  onOpenLineChart: (row: TableRow) => void
  onCreateOpportunityFromRow?: (row: TableRow) => void
  onCreateSalesSourceFromRow?: (row: TableRow) => void
  onDeleteHierarchyRow?: (row: TableRow) => void
  onAddHierarchyRow?: (row: TableRow) => void
  onAddSkuFromCatalog?: (row: TableRow) => void
  /** Reopens a line's setup (doors, cases, growth, price, start) to change it. */
  onEditLine?: (row: TableRow) => void
  onHierarchyRowNameChange?: (rowId: string, name: string) => void
  compareBadgeLabels?: { actual: string; forecast: string }
  fillForwardPeriodKeys?: string[]
}

function TableRowComponent({
  row,
  viewMode,
  periods,
  compareMode = false,
  promo_lift_compare = false,
  compareBadgeLabels,
  columnConfig,
  showCaseCostColumn,
  showForecastControlsColumn,
  onToggleExpansion,
  onDataChange,
  onForecastConfigChange,
  onOpenLineChart,
  onCreateOpportunityFromRow,
  onCreateSalesSourceFromRow,
  onDeleteHierarchyRow,
  onAddHierarchyRow,
  onAddSkuFromCatalog,
  onEditLine,
  onHierarchyRowNameChange,
  fillForwardPeriodKeys,
}: TableRowComponentProps) {
  const indentLevel = row.depth * 20
  
  // For product line view, check if this is a product line with SKU children
  const isProductLineWithSKUs = row.level === HierarchyLevel.PRODUCT_LINE && row.has_children
  const isSKU = row.level === HierarchyLevel.SKU
  
  // Determine if this row is editable (only lowest level rows). Ship-to rows are synthesized,
  // allocation-scaled clones — edits there would have no real row to land on.
  const isEditable = viewMode !== ViewMode.SHIP_TO_VIEW && (isSKU || (!row.has_children && (
    row.level === HierarchyLevel.PRODUCT_LINE ||
    row.level === HierarchyLevel.SKU ||
    row.level === HierarchyLevel.RETAILER ||
    row.level === HierarchyLevel.DISTRIBUTION_CENTER
  )))
  // A parent row (customer, product line, retailer, DC) takes a revenue, cases or units edit in a
  // plan period: the typed total is spread over the leaves beneath it by their current share
  // (`spread_parent_edit`). Store counts stay the leaves' own. Only the hierarchy view, where the
  // row id is a real node — product-line view rows are aggregates, and relationship rows have no
  // node of their own.
  const isParentEditable =
    viewMode === ViewMode.CHAIN_RETAILER_VIEW &&
    row.has_children &&
    !(row as { original_retailer_id?: string }).original_retailer_id &&
    !(row as { original_dc_id?: string }).original_dc_id
  const units_per_case = node_units_per_case(row as { metadata?: { units_per_case?: unknown } | null })
  const customer_source = (row as { metadata?: { customer_source?: CustomerOverlay } | null }).metadata?.customer_source
  const rowOpportunity = (row as any).metadata?.opportunity
  const isOpportunity = Boolean(rowOpportunity && rowOpportunity.status !== "live")
  const opportunityAccent = isOpportunity ? String(rowOpportunity.color || "#64748b").trim() : ""
  const opportunityRowFill = opportunityAccent ? opportunityRowBackgroundColor(opportunityAccent) : ""
  const opportunityCellStyle = opportunityRowFill ? { backgroundColor: opportunityRowFill } : undefined

  /**
   * Resolves a cell's "carry this forward" request against the rest of the row.
   *
   * The cell can only see its own column; which later periods are still plan (rather than actual
   * or locked) is a property of the row, so the translation happens here. Everything goes up in a
   * single call so the hierarchy is re-aggregated once instead of once per week — a weekly plan
   * is 52 columns wide.
   */
  function handleCellDataChange(
    rowId: string,
    periodKey: string,
    field: Exclude<EditableMetricField, 'case_cost'>,
    value: number,
    options?: CellEditOptions,
  ): void {
    if (!onDataChange) return
    const ordered_keys = fillForwardPeriodKeys && fillForwardPeriodKeys.length > 0
      ? fillForwardPeriodKeys
      : periods.map(period => period.period_key)
    const points = ordered_keys
      .map(period_key => ({ period_key, ...row.monthly_data[period_key] }))
      .filter(point => point.store_count !== undefined || point.cases_sold !== undefined)
    const selected = fill_forward_periods(ordered_keys, points, periodKey)

    if (!options?.apply_forward) {
      onDataChange(rowId, periodKey, field, value)
      offer_growth_forward(field, value, ordered_keys, selected)
      return
    }

    const also_period_values = fill_forward_values(selected, value, options.forward_velocity)
      .filter(entry => entry.period_key !== periodKey)

    onDataChange(rowId, periodKey, field, value, { also_period_values })

    /**
     * After a plain one-month edit on a row with growth settings, ask (never assume) whether the
     * later months should follow it under the row's own growth rules (1st Phorm, 10/29). The edit
     * itself is already applied; "Apply" re-sends it with the grown later months in one update.
     */
    function offer_growth_forward(
      edited_field: Exclude<EditableMetricField, 'case_cost'>,
      edited_value: number,
      keys: string[],
      later: ReturnType<typeof fill_forward_periods>,
    ): void {
      const config = (row as any).forecast_config
      if (row.has_children || !config || !onDataChange) return
      const grain = periods.find((period: TimePeriod) => period.period_key === periodKey)?.grain
      const months_per_period = grain === "week" ? MONTHS_PER_WEEK : grain === "quarter" ? 3 : 1
      const rules = {
        store_growth: Number(config.store_count_growth_multiplier ?? 0),
        same_store_growth: Number(config.same_store_sales_multiplier ?? 0),
        quantity_basis: point_quantity_basis(row.monthly_data[periodKey] as any),
      }
      const grown = grow_forward_values(keys, later, periodKey, edited_field as GrowableField, edited_value, rules, months_per_period)
      if (grown.length === 0) return
      const pct = (rate: number) => `${(rate * 100).toFixed(1)}%`
      const apply_to = onDataChange
      toast({
        title: "Apply this change to the rest of the months?",
        description: `${row.name}: ${grown.length} later ${grown.length === 1 ? "month" : "months"} would follow it at this line's growth (${pct(rules.store_growth)} doors, ${pct(rules.same_store_growth)} same-store a year). Actual and locked months stay as they are.`,
        action: (
          <ToastAction
            altText="Apply to the rest of the months"
            onClick={() => apply_to(rowId, periodKey, edited_field, edited_value, { also_period_values: grown })}
          >
            Apply
          </ToastAction>
        ),
      })
    }
  }

  return (
    <tr
      className={`border-b border-gray-100 ${isOpportunity ? "hover:brightness-[0.985]" : "hover:bg-gray-50"}`}
      data-opportunity-id={isOpportunity ? rowOpportunity.id : undefined}
    >
      {/* Hierarchy Column */}
      <td
        className={`p-3 sticky left-0 z-10 ${opportunityRowFill ? "" : "bg-white"}`}
        style={
          opportunityAccent
            ? { ...opportunityCellStyle, borderLeft: `4px solid ${opportunityAccent}` }
            : undefined
        }
      >
        <div
          className="flex items-start gap-2"
          style={{ paddingLeft: `${indentLevel}px` }}
        >
          <div className="flex shrink-0 items-center gap-1 pt-0.5">
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => onOpenLineChart(row)}
              className="h-7 w-7 shrink-0 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
              title="View trend chart"
            >
              <TrendingUp className="h-3.5 w-3.5" />
            </Button>
            <RowActionsMenu
              row={row}
              viewMode={viewMode}
              onAddHierarchyRow={onAddHierarchyRow}
              onAddSkuFromCatalog={onAddSkuFromCatalog}
              onCreateOpportunityFromRow={onCreateOpportunityFromRow}
              onCreateSalesSourceFromRow={onCreateSalesSourceFromRow}
              onDeleteHierarchyRow={onDeleteHierarchyRow}
            />
            {row.has_children && (
              <Button
                variant="ghost"
                onClick={() => onToggleExpansion(row.id)}
                className="p-1 h-6 w-6"
              >
                {row.is_expanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {onHierarchyRowNameChange && hierarchy_row_name_is_editable(row) ? (
                <HierarchyRowNameField
                  row={row}
                  hasChildren={row.has_children}
                  onCommit={onHierarchyRowNameChange}
                />
              ) : (
                <span className={`text-sm ${row.has_children ? "font-medium" : ""}`}>
                  {row.name}
                </span>
              )}
              {isSKU && (row as any).product_sku && (
                <span className="text-xs text-blue-600 font-mono">
                  [{(row as any).product_sku}]
                </span>
              )}
              {customer_source ? (
                <CustomerSourceBadge source={customer_source.source} netsuite_name={customer_source.netsuite_name} />
              ) : null}
              {onEditLine && row_has_line_setup(row) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-gray-400 hover:text-gray-800"
                  title="Edit this line's setup: doors, cases per store, growth, price, start month"
                  aria-label={`Edit line ${row.name}`}
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation()
                    onEditLine(row)
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              ) : null}
              {isOpportunity ? (
                <span
                  className="rounded px-1.5 py-0.5 text-xs font-medium text-gray-800"
                  style={{ backgroundColor: opportunityBadgeBackgroundColor(opportunityAccent) }}
                >
                  {rowOpportunity.likelihood_pct}% opp
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-xs leading-snug text-gray-500">
              {isSKU && (row as { metadata?: Record<string, unknown> | null }).metadata?.catalog_item
                ? pack_caption(row as { metadata?: Record<string, unknown> | null })
                : `(${formatLevelName(row.level)})`}
            </div>
          </div>
        </div>
      </td>

      {/* Time Period Columns */}
      {periods.map(period => {
        const periodData = row.monthly_data[period.period_key]
        return (
          <td key={period.period_key} className="p-3 text-center" style={opportunityCellStyle}>
            {periodData ? (
              <EditableCell
                rowId={row.id}
                periodKey={period.period_key}
                storeCount={periodData.store_count}
                skuStoreCount={periodData.sku_store_count}
                volumeDollars={periodData.volume_dollars}
                casesSold={periodData.cases_sold}
                partCases={periodData.part_cases}
                unitsSold={(periodData as any).units_sold}
                detail={(periodData as { detail?: Record<string, number> }).detail}
                isForecast={periodData.is_forecast}
                isEditable={isEditable || (isParentEditable && !periodData.is_actual)}
                storeCountEditable={!isParentEditable}
                unitsPerCase={units_per_case}
                compareMode={compareMode}
                promo_lift_compare={promo_lift_compare}
                columnConfig={columnConfig}
                comparisonData={buildPeriodComparisonData(
                  periodData,
                  compareMode,
                  columnConfig.metric_mode,
                  compareBadgeLabels
                )}
                onDataChange={handleCellDataChange}
              />
            ) : (
              <div className="text-xs text-gray-400">-</div>
            )}
          </td>
        )
      })}

      {/* Totals Column */}
      <td className="p-3 text-center" style={opportunityCellStyle}>
        <PeriodMetricsStack
          columnConfig={columnConfig}
          effective_qty={effective_quantity_mode(columnConfig)}
          volume_dollars={row.totals.total_volume}
          units_sold={row.totals.total_units}
          cases_sold={row.totals.total_cases}
          part_cases={row.totals.total_part_cases}
          store_count={row.totals.total_stores}
          sku_store_count={row.totals.total_sku_stores}
          show_promo_lift={
            promo_lift_compare &&
            !is_delivered_detail_channel(columnConfig) &&
            effective_quantity_mode(columnConfig) === "units"
          }
          promo_lift_dollars={row.totals.total_promo_lift_dollars}
          promo_lift_units={row.totals.total_promo_lift_units}
        />
      </td>

      {/* Case Cost Column */}
      {showCaseCostColumn && (
        <td className="p-3 text-center" style={opportunityCellStyle}>
          {row.level === HierarchyLevel.PRODUCT_LINE ? (
            <EditableCaseCost
              value={row.case_cost || 0}
              onChange={(value) => onDataChange?.(row.id, '', 'case_cost', value)}
            />
          ) : (
            <div className="text-sm font-medium text-gray-500">
              -
            </div>
          )}
        </td>
      )}

      {/* Forecast Multipliers Column */}
      {showForecastControlsColumn && (
        <td className="p-3 text-center" style={opportunityCellStyle}>
          <ForecastMultiplierControls
            rowId={row.id}
            forecastConfig={row.forecast_config}
            onConfigChange={(newConfig) => {
              onForecastConfigChange?.(row.id, newConfig)
            }}
          />
        </td>
      )}
    </tr>
  )
}

interface ForecastLineChartModalProps {
  row: TableRow
  periods: TimePeriod[]
  columnConfig: ColumnConfig
  onClose: () => void
}

function ForecastLineChartModal({ row, periods, columnConfig, onClose }: ForecastLineChartModalProps) {
  const chartData = useMemo(
    () => buildForecastLineChartData(row, periods),
    [row, periods]
  )

  const availableMetrics = useMemo(() => {
    const metrics: ForecastChartMetric[] = []
    if (columnConfig.show_volume !== false) metrics.push("volume_dollars")
    if (columnConfig.show_stores) metrics.push("store_count")
    const hasCases = chartData.some((p: ForecastLineChartPoint) => p.cases_sold > 0)
    const hasUnits = chartData.some((p: ForecastLineChartPoint) => p.units_sold > 0)
    if (hasCases) metrics.push("cases_sold")
    if (hasUnits) metrics.push("units_sold")
    return metrics.length > 0 ? metrics : (["volume_dollars"] as ForecastChartMetric[])
  }, [columnConfig.show_volume, columnConfig.show_stores, chartData])

  const [metric, setMetric] = useState<ForecastChartMetric>(availableMetrics[0])

  useEffect(() => {
    if (!availableMetrics.includes(metric)) {
      setMetric(availableMetrics[0])
    }
  }, [availableMetrics, metric])
  const metricLabel = getForecastChartMetricLabel(metric)
  const chartConfig = {
    [metric]: {
      label: metricLabel,
      color: "#2563eb",
    },
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e: MouseEvent) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white p-6 shadow-2xl">
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className="absolute right-4 top-4 h-8 w-8 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </Button>

        <div className="mb-4 flex items-center justify-between gap-4 pr-10">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-900">{row.name}</h2>
            <p className="text-sm text-gray-500">{formatLevelName(row.level)} trend by month</p>
          </div>
          <div className="w-40 shrink-0">
            <Select value={metric} onValueChange={(value: string) => setMetric(value as ForecastChartMetric)}>
              <SelectTrigger className="h-9 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableMetrics.map((availableMetric: ForecastChartMetric) => (
                  <SelectItem key={availableMetric} value={availableMetric}>
                    {getForecastChartMetricLabel(availableMetric)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="flex h-[340px] items-center justify-center text-sm text-gray-400">
            No monthly data for this row.
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 12, right: 24, left: 16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="period_label" tickLine={false} axisLine={false} />
                <YAxis
                  tickFormatter={(value: number) => formatForecastChartValue(value, metric)}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null
                    const value = typeof payload[0].value === "number" ? payload[0].value : parseFloat(payload[0].value) || 0
                    const point = payload[0].payload as ForecastLineChartPoint
                    return (
                      <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg">
                        <p className="mb-2 font-semibold text-gray-900">{label}</p>
                        <div className="flex items-center justify-between gap-6">
                          <span className="text-gray-600">{metricLabel}</span>
                          <span className="font-medium text-gray-900">{formatForecastChartValue(value, metric)}</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{point.source_kind}</p>
                      </div>
                    )
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={metric}
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={metricLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </div>
    </div>
  )
}

interface EditableCellProps {
  rowId: string
  periodKey: string
  storeCount: number
  skuStoreCount?: number
  volumeDollars: number
  casesSold: number
  /** Part-cases behind a zero case count on this row. */
  partCases?: number
  unitsSold?: number | null
  detail?: Record<string, number>
  isForecast: boolean
  isEditable: boolean
  /** False on a parent row: its store count is the max of its leaves' and is not typed over. */
  storeCountEditable?: boolean
  /**
   * The row's pack size. A units edit converts to cases through it (`cases = units ÷ pack`), so
   * dollars follow the way a cases edit prices them; without it a units cell shows an em dash.
   */
  unitsPerCase?: number | null
  compareMode?: boolean
  promo_lift_compare?: boolean
  columnConfig: ColumnConfig
  comparisonData?: {
    forecast_store_count: number
    forecast_volume_dollars: number
    forecast_cases_sold: number
    forecast_units_sold?: number | null
    actual_store_count: number
    actual_volume_dollars: number
    actual_cases_sold: number
    actual_units_sold?: number | null
    store_count_variance: number
    volume_dollars_variance: number
    cases_sold_variance: number
    store_count_variance_pct: number
    volume_dollars_variance_pct: number
    cases_sold_variance_pct: number
    actual_badge_label?: string
    forecast_badge_label?: string
  }
  onDataChange?: (
    rowId: string,
    periodKey: string,
    field: Exclude<EditableMetricField, 'case_cost'>,
    value: number,
    options?: CellEditOptions,
  ) => void
}

/**
 * What a cell asks for beyond the edit itself. A cell knows only its own column, so it says
 * *that* the edit should carry forward (and, for a velocity, what rate to hold); the row resolves
 * which periods that means, since only the row can see the rest of the plan.
 */
interface CellEditOptions {
  /** Write this edit into every later editable period on the same row, not just this one. */
  apply_forward?: boolean
  /**
   * Hold this units-per-store-per-week when carrying forward, re-deriving each period's quantity
   * from its own door count, instead of repeating one quantity into weeks with different stores.
   */
  forward_velocity?: number
}

/** What the row hands upward: the extra periods to write, already resolved and priced. */
interface MetricEditOptions {
  also_period_values?: { period_key: string; value: number }[]
}

function buildPeriodComparisonData(
  periodData: any,
  compareMode: boolean,
  metric_mode: MetricMode | undefined,
  compareBadgeLabels?: { actual: string; forecast: string }
): EditableCellProps["comparisonData"] | undefined {
  const mode: MetricMode = metric_mode ?? "dollars"
  if (
    !compareMode ||
    !periodData?.variance ||
    !periodData.forecast_metrics ||
    !periodData.actual_metrics ||
    periodData.compare_axis !== mode
  ) {
    return undefined
  }
  return {
    forecast_store_count: periodData.forecast_metrics.store_count ?? periodData.store_count,
    forecast_volume_dollars: periodData.forecast_metrics.volume_dollars ?? periodData.volume_dollars,
    forecast_cases_sold: periodData.forecast_metrics.cases_sold ?? periodData.cases_sold,
    forecast_units_sold: periodData.forecast_metrics.units_sold ?? null,
    actual_store_count: periodData.actual_metrics.store_count ?? periodData.store_count,
    actual_volume_dollars: periodData.actual_metrics.volume_dollars ?? periodData.volume_dollars,
    actual_cases_sold: periodData.actual_metrics.cases_sold ?? periodData.cases_sold,
    actual_units_sold: periodData.actual_metrics.units_sold ?? null,
    store_count_variance: periodData.variance.store_count_variance,
    volume_dollars_variance: periodData.variance.volume_dollars_variance,
    cases_sold_variance: periodData.variance.cases_sold_variance,
    store_count_variance_pct: periodData.variance.store_count_variance_pct,
    volume_dollars_variance_pct: periodData.variance.volume_dollars_variance_pct,
    cases_sold_variance_pct: periodData.variance.cases_sold_variance_pct,
    ...(compareBadgeLabels
      ? {
          actual_badge_label: compareBadgeLabels.actual,
          forecast_badge_label: compareBadgeLabels.forecast,
        }
      : {}),
  }
}

/**
 * The second commit button shown while a fill-forward-capable cell is being edited: leaving the
 * field saves this period alone, pressing this saves it and every later plan period.
 *
 * `onMouseDown` with `preventDefault`, not `onClick`: the input commits on blur, so letting focus
 * move would fire a plain single-period save first and this one second. Suppressing the blur and
 * committing here instead means exactly one save, and it is the one the planner asked for.
 */
function CarryForwardButton({ onCommit, label }: { onCommit: () => void; label: string }) {
  return (
    <button
      type="button"
      className="rounded px-1 text-[11px] leading-4 text-blue-600 hover:bg-blue-50"
      title={label}
      aria-label={label}
      onMouseDown={(event: MouseEvent) => {
        event.preventDefault()
        onCommit()
      }}>
      →|
    </button>
  )
}

export function EditableCell({
  rowId,
  periodKey,
  storeCount,
  skuStoreCount,
  volumeDollars,
  casesSold,
  partCases,
  unitsSold,
  detail,
  isForecast,
  isEditable,
  storeCountEditable = true,
  unitsPerCase = null,
  compareMode = false,
  promo_lift_compare = false,
  columnConfig,
  comparisonData,
  onDataChange
}: EditableCellProps) {
  const [isEditingStores, setIsEditingStores] = useState(false)
  const [isEditingVolume, setIsEditingVolume] = useState(false)
  const [isEditingCases, setIsEditingCases] = useState(false)
  const [isEditingUnits, setIsEditingUnits] = useState(false)
  const [editStoreCount, setEditStoreCount] = useState(storeCount.toString())
  const [editVolumeDollars, setEditVolumeDollars] = useState(volumeDollars.toString())
  const [editCasesSold, setEditCasesSold] = useState(casesSold.toString())
  const [editUnitsSold, setEditUnitsSold] = useState(unitsSold == null ? "" : String(unitsSold))
  const [isEditingVelocity, setIsEditingVelocity] = useState(false)
  const [editVelocity, setEditVelocity] = useState("")
  // Set by the carry-forward button's mousedown, which lands before the input's blur — so the
  // single save path that blur already triggers knows which of the two buttons was pressed.
  const applyForwardRef = useRef(false)
  // Why the last commit was refused (a negative, a fraction of a door); shown under the box,
  // which stays open until the value is fixed or Esc puts the old one back.
  const [inputError, setInputError] = useState<string | null>(null)

  function takeApplyForward(): boolean {
    const requested = applyForwardRef.current
    applyForwardRef.current = false
    return requested
  }

  /** The typed value, or `null` after showing why it was refused. */
  function readInput(raw: string, field: CellInputField): number | null {
    const parsed = parse_cell_input(raw, field)
    if (!parsed.ok) {
      setInputError(parsed.message)
      return null
    }
    setInputError(null)
    return parsed.value
  }

  function handleStoreCountSave(): boolean {
    const apply_forward = takeApplyForward()
    const newStoreCount = readInput(editStoreCount, 'store_count')
    if (newStoreCount === null) return false
    if (onDataChange) {
      onDataChange(rowId, periodKey, 'store_count', newStoreCount, apply_forward ? { apply_forward } : undefined)
    }
    setIsEditingStores(false)
    return true
  }

  function handleVolumeSave(): boolean {
    const newVolumeDollars = readInput(editVolumeDollars, 'volume_dollars')
    if (newVolumeDollars === null) return false
    if (onDataChange) {
      onDataChange(rowId, periodKey, 'volume_dollars', newVolumeDollars)
    }
    setIsEditingVolume(false)
    return true
  }

  function handleStoreCountCancel() {
    setInputError(null)
    setEditStoreCount(storeCount.toString())
    setIsEditingStores(false)
  }

  function handleVolumeCancel() {
    setInputError(null)
    setEditVolumeDollars(volumeDollars.toString())
    setIsEditingVolume(false)
  }

  function handleCasesSave(): boolean {
    const newCasesSold = readInput(editCasesSold, 'cases_sold')
    if (newCasesSold === null) return false
    if (onDataChange) {
      onDataChange(rowId, periodKey, 'cases_sold', newCasesSold)
    }
    setIsEditingCases(false)
    return true
  }

  function handleCasesCancel() {
    setInputError(null)
    setEditCasesSold(casesSold.toString())
    setIsEditingCases(false)
  }

  /**
   * A units edit on a row with a pack size is a cases edit in disguise: `units ÷ units_per_case`
   * cases (fractional, to the hundredth), priced the way a cases edit prices them. A row with no
   * pack size (actualizer units, or a parent whose leaves each convert through their own pack)
   * takes the unit count as typed.
   */
  function commit_units(units: number, options?: CellEditOptions): void {
    if (!onDataChange) return
    const as_cases = cases_from_units(units, unitsPerCase)
    if (as_cases !== null) {
      const forward_velocity =
        options?.forward_velocity !== undefined && unitsPerCase ? options.forward_velocity / unitsPerCase : undefined
      onDataChange(rowId, periodKey, 'cases_sold', as_cases, options ? { ...options, forward_velocity } : undefined)
      return
    }
    onDataChange(rowId, periodKey, 'units_sold', units, options)
  }

  function handleUnitsSave(): boolean {
    const newUnitsSold = readInput(editUnitsSold, 'units_sold')
    if (newUnitsSold === null) return false
    commit_units(newUnitsSold)
    setIsEditingUnits(false)
    return true
  }

  function handleUnitsCancel() {
    setInputError(null)
    setEditUnitsSold(unitsSold == null ? "" : String(unitsSold))
    setIsEditingUnits(false)
  }

  /**
   * Commit a velocity: hold the door count and move the quantity to match.
   *
   * This writes the ordinary `cases_sold` / `units_sold` field, so a velocity edit is
   * indistinguishable downstream from typing the quantity by hand — same aggregation, same
   * lock rules, same changelog entry.
   */
  function handleVelocitySave(): boolean {
    const apply_forward = takeApplyForward()
    const parsed = readInput(editVelocity, 'velocity')
    if (parsed === null) return false
    setIsEditingVelocity(false)
    if (!onDataChange) return true
    commitVelocity(parsed, apply_forward)
    return true
  }

  function commitVelocity(parsed: number, apply_forward: boolean): void {
    if (!onDataChange) return
    const quantity_mode = effective_quantity_mode(columnConfig)
    const current = quantity_mode === "cases" ? casesSold : unitsSold
    // A carry-forward is a real change even where this period's own quantity is unmoved: the
    // later weeks are what the planner is after.
    if (!apply_forward && !velocity_edit_changes_quantity(parsed, current, storeCount)) return
    const next = quantity_from_velocity(parsed, storeCount)
    if (next == null) return
    // Carrying a velocity forward holds the rate, not the quantity — each later week re-derives
    // its own number from its own door count.
    const options = apply_forward ? { apply_forward, forward_velocity: parsed } : undefined
    if (quantity_mode === "units") {
      commit_units(next, options)
      return
    }
    onDataChange(rowId, periodKey, 'cases_sold', next, options)
  }

  function handleVelocityCancel() {
    setInputError(null)
    setEditVelocity("")
    setIsEditingVelocity(false)
  }

  /** Clicking out of a box puts the old value back; only Enter or Tab commits. */
  function handleBlur(type: 'stores' | 'volume' | 'cases' | 'units' | 'velocity') {
    if (type === 'stores') handleStoreCountCancel()
    else if (type === 'volume') handleVolumeCancel()
    else if (type === 'units') handleUnitsCancel()
    else if (type === 'velocity') handleVelocityCancel()
    else handleCasesCancel()
  }

  function handleKeyDown(e: KeyboardEvent, type: 'stores' | 'volume' | 'cases' | 'units' | 'velocity') {
    const action = inline_edit_key_action(e.key)
    if (action === 'commit') {
      let committed: boolean
      if (type === 'stores') {
        committed = handleStoreCountSave()
      } else if (type === 'volume') {
        committed = handleVolumeSave()
      } else if (type === 'units') {
        committed = handleUnitsSave()
      } else if (type === 'velocity') {
        committed = handleVelocitySave()
      } else {
        committed = handleCasesSave()
      }
      // A refused value keeps the box (and its message) on screen; Tab must not carry focus away,
      // or the blur would throw the typed value out before it can be fixed.
      if (!committed) {
        e.preventDefault()
      }
    } else if (action === 'cancel') {
      if (type === 'stores') {
        handleStoreCountCancel()
      } else if (type === 'volume') {
        handleVolumeCancel()
      } else if (type === 'units') {
        handleUnitsCancel()
      } else if (type === 'velocity') {
        handleVelocityCancel()
      } else {
        handleCasesCancel()
      }
    }
  }

  if (compareMode && comparisonData) {
    return (
      <ExpandableComparison
        metric_mode={columnConfig.metric_mode ?? "dollars"}
        comparisonData={comparisonData}
      />
    )
  }

  const effective_qty = effective_quantity_mode(columnConfig)
  if (
    promo_lift_compare &&
    !is_delivered_detail_channel(columnConfig) &&
    effective_qty === "units" &&
    detail &&
    detail_has_positive_forecasted_promo(detail)
  ) {
    const promo = detail[FORECAST_DETAIL.incremental_promo_units] as number
    const lift_dollars = imputed_promo_lift_revenue(volumeDollars, unitsSold, promo) ?? 0
    return (
      <div className="mx-auto w-max">
        <ForecastPromoMetricRows
          show_volume={columnConfig.show_volume !== false}
          volume_dollars={volumeDollars}
          promo_lift_dollars={lift_dollars}
          show_stores={columnConfig.show_stores}
          store_count={storeCount}
          base_units_display={unitsSold == null ? "—" : Number(unitsSold).toLocaleString()}
          promo_units={promo}
        />
      </div>
    )
  }

  if (!isEditable) {
    return (
      <PeriodMetricsStack
        columnConfig={columnConfig}
        effective_qty={effective_qty}
        volume_dollars={volumeDollars}
        units_sold={unitsSold}
        cases_sold={casesSold}
        part_cases={partCases}
        store_count={storeCount}
        sku_store_count={skuStoreCount}
        show_promo_lift={
          !!promo_lift_compare &&
          !is_delivered_detail_channel(columnConfig) &&
          effective_qty === "units"
        }
        promo_lift_dollars={cell_imputed_promo_dollars(detail, volumeDollars, unitsSold)}
        promo_lift_units={cell_promo_incremental_units(detail)}
      />
    )
  }

  return (
    <div className="space-y-1">
      {columnConfig.show_volume !== false && (
        <>
          {isEditingVolume ? (
            <Input
              type="number"
              step="any"
              value={editVolumeDollars}
              onChange={(e) => setEditVolumeDollars(e.currentTarget.value)}
              onKeyDown={(e) => handleKeyDown(e, 'volume')}
              onBlur={() => handleBlur("volume")}
              className="w-full h-5 text-xs"
              placeholder="Volume"
              autoFocus
            />
          ) : (
            <div 
              className={`${REVENUE_CELL_CLASS} cursor-pointer hover:bg-blue-50 hover:border hover:border-blue-200 rounded px-1 py-0.5 transition-colors`}
              onClick={() => {
                setEditVolumeDollars(volumeDollars.toString())
                setIsEditingVolume(true)
              }}
            >
              {exactCurrencyFormatter(volumeDollars)}
            </div>
          )}
        </>
      )}

      {(columnConfig.show_stores || effective_qty === "cases" || effective_qty === "units") && (
        <div className="flex items-center justify-center gap-1 flex-wrap">
          {columnConfig.show_stores && !storeCountEditable ? (
            <div
              className={`rounded px-1 py-0.5 ${QUANTITY_CELL_CLASS}`}
              title="A parent row's store count is its rows' widest reach — edit doors on the rows below"
            >
              {storeCount.toLocaleString()}
            </div>
          ) : null}
          {columnConfig.show_stores && storeCountEditable && (
            <>
              {isEditingStores ? (
                <span className="inline-flex items-center gap-0.5">
                  <Input
                    type="number"
                    value={editStoreCount}
                    onChange={(e) => setEditStoreCount(e.currentTarget.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'stores')}
                    onBlur={() => handleBlur("stores")}
                    className="w-16 h-6 text-xs font-medium"
                    placeholder="Stores"
                    autoFocus
                  />
                  <CarryForwardButton
                    label="Apply this store count to this period and every later one"
                    onCommit={() => {
                      applyForwardRef.current = true
                      handleStoreCountSave()
                    }}
                  />
                </span>
              ) : (
                <div 
                  className={`${QUANTITY_CELL_CLASS} cursor-pointer hover:bg-blue-50 hover:border hover:border-blue-200 rounded px-1 py-0.5 transition-colors`}
                  onClick={() => {
                    setEditStoreCount(storeCount.toString())
                    setIsEditingStores(true)
                  }}
                >
                  {storeCount.toLocaleString()}
                </div>
              )}
            </>
          )}

          {columnConfig.show_stores && (effective_qty === "cases" || effective_qty === "units") ? (
            <span className={QUANTITY_CELL_CLASS}>×</span>
          ) : null}

          {effective_qty === "cases" ? (
            <>
              {isEditingCases ? (
                <Input
                  type="number"
                  step="any"
                  value={editCasesSold}
                  onChange={(e) => setEditCasesSold(e.currentTarget.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'cases')}
                  onBlur={() => handleBlur("cases")}
                  className="w-16 h-6 text-xs font-medium"
                  placeholder="Cases"
                  autoFocus
                />
              ) : (
                <div 
                  className={`${QUANTITY_CELL_CLASS} cursor-pointer hover:bg-blue-50 hover:border hover:border-blue-200 rounded px-1 py-0.5 transition-colors`}
                  onClick={() => {
                    setEditCasesSold(casesSold.toString())
                    setIsEditingCases(true)
                  }}
                >
                  {cases_display(casesSold, partCases)}
                </div>
              )}
            </>
          ) : (
            <>
              {isEditingUnits ? (
                <Input
                  type="number"
                  step="any"
                  value={editUnitsSold}
                  onChange={(e) => setEditUnitsSold(e.currentTarget.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'units')}
                  onBlur={() => handleBlur("units")}
                  className="w-16 h-6 text-xs font-medium"
                  placeholder="Units"
                  autoFocus
                />
              ) : (
                <span
                  className={`${QUANTITY_CELL_CLASS} tabular-nums rounded px-1 py-0.5 transition-colors ${
                    unitsSold != null || unitsPerCase
                      ? "cursor-pointer hover:bg-blue-50 hover:border hover:border-blue-200"
                      : "cursor-default text-gray-400"
                  }`}
                  title={unitsSold == null && !unitsPerCase ? UNITS_PER_CASE_UNKNOWN_HINT : undefined}
                  onClick={() => {
                    // Nothing stored and no pack size: there is no unit to type into.
                    if (unitsSold == null && !unitsPerCase) return
                    setEditUnitsSold(unitsSold == null ? "" : String(unitsSold))
                    setIsEditingUnits(true)
                  }}
                >
                  {unitsSold == null ? "—" : Number(unitsSold).toLocaleString()}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {columnConfig.show_velocity && columnConfig.show_stores ? (
        isEditingVelocity ? (
          <span className="mx-auto flex w-fit items-center gap-0.5">
            <Input
              type="number"
              step="0.01"
              value={editVelocity}
              onChange={(e) => setEditVelocity(e.currentTarget.value)}
              onKeyDown={(e) => handleKeyDown(e, 'velocity')}
              onBlur={() => handleBlur("velocity")}
              className="h-5 w-20 text-xs"
              placeholder="Per store"
              autoFocus
            />
            <CarryForwardButton
              label="Hold this per-store rate for this period and every later one"
              onCommit={() => {
                applyForwardRef.current = true
                handleVelocitySave()
              }}
            />
          </span>
        ) : (
          <div
            className="cursor-pointer rounded px-1 py-0.5 text-[11px] tabular-nums text-gray-500 transition-colors hover:border hover:border-blue-200 hover:bg-blue-50"
            title={
              storeCount > 0
                ? `${effective_qty === "cases" ? "Cases" : "Units"} per store — editing this holds the store count and moves the quantity`
                : "Set a store count before planning by velocity"
            }
            onClick={() => {
              const current = compute_velocity(effective_qty === "cases" ? casesSold : unitsSold, storeCount)
              setEditVelocity(current == null ? "" : current.toFixed(2))
              setIsEditingVelocity(true)
            }}
          >
            {format_velocity(compute_velocity(effective_qty === "cases" ? casesSold : unitsSold, storeCount))}/store
          </div>
        )
      ) : null}
      {inputError ? (
        <p role="alert" className="max-w-[10rem] text-[10px] leading-tight text-red-600">
          {inputError}
        </p>
      ) : null}
    </div>
  )
}

// Utility functions
function data_point_has_units(dataPoint: SalesDataPoint): boolean {
  const m = dataPoint.metrics
  if (!m) return false
  if (typeof m.units_sold === "number" && m.units_sold > 0) return true
  const d = m.detail
  if (!d || typeof d !== "object") return false
  for (const k of [
    FORECAST_DETAIL.forecasted_consumption_units,
    FORECAST_DETAIL.seasonality_baseline_units,
    FORECAST_DETAIL.incremental_promo_units,
  ] as const) {
    const v = (d as Record<string, number | undefined>)[k]
    if (typeof v === "number" && v !== 0) return true
  }
  return false
}

function getAvailableColumns(nodes: HierarchyNode[]): TableColumnAvailability {
  const availableColumns: TableColumnAvailability = {
    show_stores: false,
    show_cases: false,
    show_volume: false,
    show_units: false,
  }

  function inspectNode(node: HierarchyNode): void {
    node.sales_data.forEach((dataPoint: SalesDataPoint) => {
      availableColumns.show_stores = availableColumns.show_stores || dataPoint.metrics.store_count > 0
      availableColumns.show_cases = availableColumns.show_cases || dataPoint.metrics.cases_sold > 0
      availableColumns.show_volume = availableColumns.show_volume || dataPoint.metrics.volume_dollars > 0
      availableColumns.show_units = availableColumns.show_units || data_point_has_units(dataPoint)
    })
    node.children?.forEach(inspectNode)
  }

  nodes.forEach(inspectNode)
  return availableColumns
}

function formatLevelName(level: HierarchyLevel): string {
  switch (level) {
    case HierarchyLevel.CLASS_OF_TRADE:
      return 'Class of Trade'
    case HierarchyLevel.DISTRIBUTOR:
      return 'Distributor'
    case HierarchyLevel.DISTRIBUTION_CENTER:
      return 'DC'
    case HierarchyLevel.RETAILER:
      return 'Retailer'
    case HierarchyLevel.PRODUCT_LINE:
      return 'Product Line'
    case HierarchyLevel.SKU:
      return 'SKU'
    default:
      return level
  }
}

// Forecast Multiplier Controls Component
interface ForecastMultiplierControlsProps {
  rowId: string
  forecastConfig?: {
    same_store_sales_multiplier: number
    store_count_growth_multiplier: number
  }
  onConfigChange: (newConfig: {
    same_store_sales_multiplier: number
    store_count_growth_multiplier: number
  }) => void
}

function ForecastMultiplierControls({ 
  rowId, 
  forecastConfig, 
  onConfigChange 
}: ForecastMultiplierControlsProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [sssMultiplier, setSssMultiplier] = useState(
    forecastConfig?.same_store_sales_multiplier != null ? (forecastConfig.same_store_sales_multiplier * 100).toFixed(1) : '3.0'
  )
  const [storeMultiplier, setStoreMultiplier] = useState(
    forecastConfig?.store_count_growth_multiplier != null ? (forecastConfig.store_count_growth_multiplier * 100).toFixed(1) : '2.0'
  )

  function handleSave() {
    const newConfig = {
      same_store_sales_multiplier: parseFloat(sssMultiplier) / 100,
      store_count_growth_multiplier: parseFloat(storeMultiplier) / 100
    }
    onConfigChange(newConfig)
    setIsEditing(false)
  }

  function handleCancel() {
    setSssMultiplier(forecastConfig?.same_store_sales_multiplier != null ? (forecastConfig.same_store_sales_multiplier * 100).toFixed(1) : '3.0')
    setStoreMultiplier(forecastConfig?.store_count_growth_multiplier != null ? (forecastConfig.store_count_growth_multiplier * 100).toFixed(1) : '2.0')
    setIsEditing(false)
  }

  if (!forecastConfig) {
    return <div className="text-xs text-gray-400">-</div>
  }

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={storeMultiplier}
            onChange={(e) => setStoreMultiplier(e.currentTarget.value)}
            className="w-12 h-6 text-xs"
            placeholder="Stores%"
            step="0.1"
            min="0"
            max="100"
          />
          <span className="text-xs text-gray-500">%</span>
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={sssMultiplier}
            onChange={(e) => setSssMultiplier(e.currentTarget.value)}
            className="w-12 h-6 text-xs"
            placeholder="SSS%"
            step="0.1"
            min="0"
            max="100"
          />
          <span className="text-xs text-gray-500">%</span>
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            onClick={handleSave}
            className="h-6 px-2 text-xs"
          >
            ✓
          </Button>
          <Button
            variant="outline"
            onClick={handleCancel}
            className="h-6 px-2 text-xs"
          >
            ✕
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="space-y-1 cursor-pointer hover:bg-gray-50 rounded p-1"
      onClick={() => setIsEditing(true)}
    >
      <div className="text-xs font-medium text-gray-700">
        {(forecastConfig.store_count_growth_multiplier * 100).toFixed(1)}%
      </div>
      <div className="text-xs text-gray-600">
        {(forecastConfig.same_store_sales_multiplier * 100).toFixed(1)}%
      </div>
    </div>
  )
}


// Editable Case Cost Component
interface EditableCaseCostProps {
  value: number
  onChange: (value: number) => void
}

function EditableCaseCost({ value, onChange }: EditableCaseCostProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value.toString())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEditValue(value.toString())
  }, [value])

  function handleSubmit(): boolean {
    const parsed = parse_cell_input(editValue, "case_cost")
    if (!parsed.ok) {
      setError(parsed.message)
      return false
    }
    setError(null)
    onChange(parsed.value)
    setIsEditing(false)
    return true
  }

  function handleCancel() {
    setError(null)
    setEditValue(value.toString())
    setIsEditing(false)
  }

  function handleKeyDown(e: KeyboardEvent) {
    const action = inline_edit_key_action(e.key)
    if (action === "commit") {
      if (!handleSubmit()) e.preventDefault()
    } else if (action === "cancel") {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className="flex flex-col items-center">
        <Input
          type="number"
          step="any"
          value={editValue}
          onChange={(e) => setEditValue((e.target as HTMLInputElement).value)}
          onBlur={handleCancel}
          onKeyDown={handleKeyDown}
          className="h-7 min-w-20 text-center text-sm font-medium text-gray-700"
          autoFocus
        />
        {error ? <p role="alert" className="mt-0.5 max-w-[10rem] text-[10px] leading-tight text-red-600">{error}</p> : null}
      </div>
    )
  }

  // Ensure value is a number
  const numericValue = typeof value === 'number' ? value : (typeof value === 'string' ? parseFloat(value) : 0)
  
  return (
    <div
      className="text-sm font-medium text-gray-700 min-w-20 cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
      onClick={() => setIsEditing(true)}
    >
      ${numericValue.toFixed(2)}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe: describeMix, expect: expectMix, it: itMix } = import.meta.vitest

  describeMix("row_has_line_setup", () => {
    itMix("offers Edit line on a leaf generated from a setup, not on roll-ups or rows without one", () => {
      expectMix(row_has_line_setup({ has_children: false, forecast_config: { initial_doors: 10 } } as any)).toBe(true)
      expectMix(row_has_line_setup({ has_children: true, forecast_config: { initial_doors: 10 } } as any)).toBe(false)
      expectMix(row_has_line_setup({ has_children: false } as any)).toBe(false)
    })
  })

  describeMix("hierarchy_row_name_is_editable", () => {
    itMix("keeps a product row's name fixed and lets made-up rows be renamed", () => {
      expectMix(hierarchy_row_name_is_editable({ level: HierarchyLevel.SKU } as TableRow)).toBe(false)
      expectMix(hierarchy_row_name_is_editable({ level: HierarchyLevel.PRODUCT_LINE } as TableRow)).toBe(true)
      expectMix(hierarchy_row_name_is_editable({ level: HierarchyLevel.CUSTOMER } as TableRow)).toBe(true)
      expectMix(hierarchy_row_name_is_editable({ level: HierarchyLevel.CUSTOMER, original_retailer_id: "r1" } as TableRow)).toBe(false)
    })
  })

  describeMix("mixHexWithWhite", () => {
    itMix("lightens pure red toward pink-gray at half blend", () => {
      expectMix(mixHexWithWhite("#ff0000", 0.5)).toBe("rgb(255, 128, 128)")
    })

    itMix("returns neutral gray for invalid hex", () => {
      expectMix(mixHexWithWhite("nope", 0.14)).toBe("rgb(243, 244, 246)")
    })
  })

  describeMix("buildForecastLineChartData", () => {
    const row: TableRow = {
      id: "row-1",
      name: "Retailer A",
      level: HierarchyLevel.RETAILER,
      depth: 0,
      is_expanded: false,
      is_visible: true,
      has_children: false,
      monthly_data: {
        "2026-01": {
          store_count: 10,
          volume_dollars: 1200,
          cases_sold: 40,
          is_forecast: false,
          is_actual: true,
        },
        "2026-02": {
          store_count: 12,
          volume_dollars: 1500,
          cases_sold: 50,
          is_forecast: true,
          is_actual: false,
        },
      },
      totals: {
        total_stores: 22,
        total_volume: 2700,
        total_cases: 90,
        total_units: 0,
        total_promo_lift_dollars: 0,
        total_promo_lift_units: 0,
        average_stores_per_period: 11,
        average_volume_per_period: 1350,
        average_cases_per_period: 45,
      },
    }

    itMix("returns visible periods in table order with display labels and source kind", () => {
      const points = buildForecastLineChartData(row, [
        { year: 2026, month: 2, period_key: "2026-02" },
        { year: 2026, month: 3, period_key: "2026-03" },
        { year: 2026, month: 1, period_key: "2026-01" },
      ])

      expect(points.map((point: ForecastLineChartPoint) => point.period_key)).toEqual(["2026-02", "2026-01"])
      expect(points[0].period_label).toBe("Feb 2026")
      expect(points[0].source_kind).toBe("Forecast")
      expect(points[1].source_kind).toBe("Actual")
    })
  })
}
